import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { toast } from 'react-toastify';
import clsx from 'clsx';
import Select from 'react-select';
import { shallowEqual, useSelector } from 'react-redux';

import {
  listStoreListings,
  updateStoreListing,
} from '../services/StoreListingCRUD';
import { StoreListingModel } from '../models/StoreListingModel';
import authorizedStoreListingUsers from '../constants/authorizedStoreListingUsers';
import { authUserSelector } from '../../auth';
import { listStores, Store } from '../../plans/services/PlanCRUD';
import StoreListingModalForm from './StoreListingModalForm';

type StateFilter = 'active' | 'inactive' | null;
type VisibilityFilter = 'visible' | 'hidden' | null;

interface ProductOption {
  value: string;
  label: string;
}

function productKey(storeListing: StoreListingModel): string {
  return storeListing.ean ?? storeListing.name;
}

function productLabel(storeListing: StoreListingModel): string {
  return storeListing.ean
    ? `${storeListing.name} - ${storeListing.ean}`
    : storeListing.name;
}

enum StoreListingState {
  IDLE = 'idle',
  LOADING = 'loading',
}

export default function StoreListingTable() {
  const intl = useIntl();
  const authUser = useSelector(authUserSelector, shallowEqual);
  const canEdit = authorizedStoreListingUsers.includes(authUser?.email);
  const [state, setState] = useState<StoreListingState>(
    StoreListingState.LOADING
  );
  const [storeListings, setStoreListings] = useState<StoreListingModel[]>([]);
  const [editingPriceId, setEditingPriceId] = useState<number | null>(null);
  const [editingPrice, setEditingPrice] = useState<string>('');
  const [productFilter, setProductFilter] = useState<ProductOption | null>(
    null
  );
  const [stateFilter, setStateFilter] = useState<StateFilter>(null);
  const [visibilityFilter, setVisibilityFilter] =
    useState<VisibilityFilter>(null);
  const [shopFilter, setShopFilter] = useState<string | null>(null);
  const [stores, setStores] = useState<Store[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);

  async function fetchStores() {
    try {
      const { data } = await listStores();
      setStores(data);
    } catch (error) {
      console.error('Error fetching stores:', error);
    }
  }

  async function fetchStoreListings() {
    setState(StoreListingState.LOADING);
    try {
      const { data } = await listStoreListings();
      setStoreListings(data);
    } catch (error) {
      console.error('Error fetching store listings:', error);
      toast.error(intl.formatMessage({ id: 'storeListing.fetchError' }));
    } finally {
      setState(StoreListingState.IDLE);
    }
  }

  async function toggleState(storeListing: StoreListingModel) {
    if (!storeListing.id) {
      return;
    }

    try {
      const newState = storeListing.state === 'active' ? 'inactive' : 'active';
      await updateStoreListing({
        id: storeListing.id,
        state: newState,
      });
      setStoreListings((prevListings) =>
        prevListings.map((listing) =>
          listing.id === storeListing.id
            ? {
                ...listing,
                state: newState,
              }
            : listing
        )
      );
      toast.success(
        intl.formatMessage({ id: 'storeListing.toggleActiveSuccess' })
      );
    } catch (error) {
      toast.error(intl.formatMessage({ id: 'storeListing.toggleActiveError' }));
    }
  }

  async function toggleVisibility(storeListing: StoreListingModel) {
    if (!storeListing.id) {
      return;
    }

    try {
      const newVisibility = !storeListing.is_visible_to_customer;
      await updateStoreListing({
        id: storeListing.id,
        is_visible_to_customer: newVisibility,
      });
      setStoreListings((prevListings) =>
        prevListings.map((listing) =>
          listing.id === storeListing.id
            ? {
                ...listing,
                is_visible_to_customer: newVisibility,
              }
            : listing
        )
      );
      toast.success(
        intl.formatMessage({ id: 'storeListing.toggleVisibilitySuccess' })
      );
    } catch (error) {
      toast.error(
        intl.formatMessage({ id: 'storeListing.toggleVisibilityError' })
      );
    }
  }

  function startEditingPrice(storeListing: StoreListingModel) {
    setEditingPriceId(storeListing.id);
    setEditingPrice(storeListing.price.toFixed(2));
  }

  function cancelEditingPrice() {
    setEditingPriceId(null);
    setEditingPrice('');
  }

  async function savePrice(storeListing: StoreListingModel) {
    if (!storeListing.id) {
      setEditingPriceId(null);
      setEditingPrice('');
      return;
    }

    const newPrice = parseFloat(editingPrice);
    if (isNaN(newPrice) || newPrice < 0) {
      toast.error(intl.formatMessage({ id: 'storeListing.invalidPrice' }));
      return;
    }

    try {
      await updateStoreListing({
        id: storeListing.id,
        price: newPrice,
      });
      setStoreListings((prevListings) =>
        prevListings.map((listing) =>
          listing.id === storeListing.id
            ? {
                ...listing,
                price: newPrice,
              }
            : listing
        )
      );
      toast.success(
        intl.formatMessage({ id: 'storeListing.priceUpdatedSuccess' })
      );
      cancelEditingPrice();
    } catch (error) {
      toast.error(intl.formatMessage({ id: 'storeListing.priceUpdateError' }));
    }
  }

  useEffect(() => {
    fetchStores();
    fetchStoreListings();
  }, []);

  const productOptions: ProductOption[] = Array.from(
    new Map(
      storeListings.map((l) => [
        productKey(l),
        { value: productKey(l), label: productLabel(l) },
      ])
    ).values()
  ).sort((a, b) => a.label.localeCompare(b.label));

  const uniqueShops = Array.from(
    new Set(storeListings.map((l) => l.store.name))
  ).sort((a, b) => a.localeCompare(b));

  const filteredStoreListings = storeListings.filter((l) => {
    if (productFilter && productKey(l) !== productFilter.value) return false;
    if (stateFilter && l.state !== stateFilter) return false;
    if (visibilityFilter === 'visible' && !l.is_visible_to_customer)
      return false;
    if (visibilityFilter === 'hidden' && l.is_visible_to_customer) return false;
    if (shopFilter && l.store.name !== shopFilter) return false;
    return true;
  });

  return (
    <>
      <div className="card p-10 w-100">
        <div className="d-flex align-items-center justify-content-between mb-8">
          <h1 className="mb-0">
            {intl.formatMessage({ id: 'storeListing.title' })}
          </h1>
          {canEdit && (
            <button
              className="btn btn-primary"
              onClick={() => setShowCreateModal(true)}
            >
              <span className="fa fa-plus me-2"></span>
              {intl.formatMessage({ id: 'storeListing.addStoreListing' })}
            </button>
          )}
        </div>

        {state === StoreListingState.IDLE && storeListings?.length > 0 && (
          <div className="d-flex flex-wrap gap-4 mb-6">
            <div className="form-group w-300px">
              <label className="form-label mb-2">
                {intl.formatMessage({ id: 'storeListing.filterProduct' })}
              </label>
              <Select
                className="react-select-container"
                classNamePrefix="react-select"
                isSearchable
                isClearable
                placeholder={intl.formatMessage({
                  id: 'storeListing.allProducts',
                })}
                options={productOptions}
                value={productFilter}
                onChange={(opt) => setProductFilter(opt)}
                noOptionsMessage={() =>
                  intl.formatMessage({ id: 'storeListing.noProductsFound' })
                }
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 1030 }),
                }}
              />
            </div>

            <div className="form-group w-200px">
              <label className="form-label mb-2">
                {intl.formatMessage({ id: 'storeListing.filterState' })}
              </label>
              <select
                className="form-select"
                value={stateFilter ?? ''}
                onChange={(e) =>
                  setStateFilter((e.target.value || null) as StateFilter)
                }
              >
                <option value="">
                  {intl.formatMessage({ id: 'storeListing.allStates' })}
                </option>
                <option value="active">
                  {intl.formatMessage({ id: 'storeListing.stateActive' })}
                </option>
                <option value="inactive">
                  {intl.formatMessage({ id: 'storeListing.stateInactive' })}
                </option>
              </select>
            </div>

            <div className="form-group w-220px">
              <label className="form-label mb-2">
                {intl.formatMessage({ id: 'storeListing.filterVisibility' })}
              </label>
              <select
                className="form-select"
                value={visibilityFilter ?? ''}
                onChange={(e) =>
                  setVisibilityFilter(
                    (e.target.value || null) as VisibilityFilter
                  )
                }
              >
                <option value="">
                  {intl.formatMessage({ id: 'storeListing.allVisibilities' })}
                </option>
                <option value="visible">
                  {intl.formatMessage({ id: 'storeListing.visible' })}
                </option>
                <option value="hidden">
                  {intl.formatMessage({ id: 'storeListing.notVisible' })}
                </option>
              </select>
            </div>

            <div className="form-group w-260px">
              <label className="form-label mb-2">
                {intl.formatMessage({ id: 'storeListing.filterShop' })}
              </label>
              <select
                className="form-select"
                value={shopFilter ?? ''}
                onChange={(e) => setShopFilter(e.target.value || null)}
              >
                <option value="">
                  {intl.formatMessage({ id: 'storeListing.allShops' })}
                </option>
                {uniqueShops.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        {state === StoreListingState.LOADING && (
          <div className="d-flex justify-content-center py-10">
            <div className="spinner-border text-primary" role="status" />
          </div>
        )}

        {state === StoreListingState.IDLE && !storeListings?.length && (
          <p className="mt-8">
            {intl.formatMessage({ id: 'storeListing.noStoreListings' })}
          </p>
        )}

        {state === StoreListingState.IDLE &&
          storeListings?.length > 0 &&
          filteredStoreListings.length === 0 && (
            <p className="mt-8">
              {intl.formatMessage({ id: 'storeListing.noFilterResults' })}
            </p>
          )}

        {state === StoreListingState.IDLE &&
          filteredStoreListings.length > 0 && (
            <div
              className="table-responsive mt-8"
              style={{ maxHeight: 'calc(100vh - 320px)' }}
            >
              <table className="table table-hover table-row-dashed table-row-gray-200 align-middle gs-0 gy-4">
                <thead
                  className="table-header sticky-top bg-white"
                  style={{ zIndex: 2 }}
                >
                  <tr className="fw-bolder">
                    <th>{intl.formatMessage({ id: 'storeListing.ean' })}</th>
                    <th>{intl.formatMessage({ id: 'storeListing.state' })}</th>
                    <th>
                      {intl.formatMessage({
                        id: 'storeListing.visibleToCustomer',
                      })}
                    </th>
                    <th>
                      {intl.formatMessage({ id: 'storeListing.country' })}
                    </th>
                    <th>{intl.formatMessage({ id: 'storeListing.store' })}</th>
                    <th>
                      {intl.formatMessage({ id: 'storeListing.product' })}
                    </th>
                    <th className="text-end">
                      {intl.formatMessage({ id: 'storeListing.price' })}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredStoreListings.map((storeListing) => (
                    <tr
                      key={storeListing.id}
                      className={clsx({
                        'align-middle': true,
                        'opacity-50': storeListing.state === 'inactive',
                      })}
                    >
                      <td>{storeListing.ean || '-'}</td>
                      <td>
                        <div className="form-check form-switch">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={storeListing.state === 'active'}
                            onChange={() => toggleState(storeListing)}
                            disabled={!canEdit}
                          />
                        </div>
                      </td>
                      <td>
                        <div className="form-check form-switch">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={storeListing.is_visible_to_customer}
                            onChange={() => toggleVisibility(storeListing)}
                            disabled={!canEdit}
                          />
                        </div>
                      </td>
                      <td>{storeListing.store.country}</td>
                      <td>{storeListing.store.name}</td>
                      <td>{storeListing.name}</td>
                      <td className="text-end">
                        {editingPriceId === storeListing.id ? (
                          <div className="d-flex align-items-center justify-content-end gap-2">
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              className="form-control form-control-sm w-100px"
                              value={editingPrice}
                              onChange={(e) => setEditingPrice(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  savePrice(storeListing);
                                } else if (e.key === 'Escape') {
                                  cancelEditingPrice();
                                }
                              }}
                              autoFocus
                            />
                            <button
                              className="btn btn-sm btn-success px-2 py-1"
                              onClick={() => savePrice(storeListing)}
                            >
                              <i className="fas fa-check"></i>
                            </button>
                            <button
                              className="btn btn-sm btn-secondary px-2 py-1"
                              onClick={cancelEditingPrice}
                            >
                              <i className="fas fa-times"></i>
                            </button>
                          </div>
                        ) : (
                          <div className="d-flex align-items-center justify-content-end gap-2">
                            <span>{storeListing.price.toFixed(2)}€</span>
                            {canEdit && (
                              <span
                                className="d-block cursor-pointer px-2 py-1"
                                onClick={() => startEditingPrice(storeListing)}
                              >
                                <i className="fas fa-edit"></i>
                              </span>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </div>
      <StoreListingModalForm
        stores={stores}
        visible={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreated={() => {
          setShowCreateModal(false);
          fetchStoreListings();
        }}
      />
    </>
  );
}
